import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import BeforeAfter from "@/components/landing/BeforeAfter";
import FinalCTA from "@/components/landing/FinalCTA";
import { Zap, GitBranch, AlertTriangle, Layers, Target, TrendingUp } from "lucide-react";

const CHALLENGES = [
  {
    icon: Zap,
    num: "01",
    title: "Hipercrecimiento que genera caos",
    text: "Escalar rápido es genial — hasta que la estructura no acompaña. Más personas, más procesos, más decisiones sin dueño. La velocidad se convierte en ruido.",
  },
  {
    icon: GitBranch,
    num: "02",
    title: "Producto y negocio hablan idiomas distintos",
    text: "El equipo técnico ejecuta con agilidad. El equipo comercial empuja por resultados. Sin un marco compartido de visión y prioridades, el conflicto es permanente.",
  },
  {
    icon: AlertTriangle,
    num: "03",
    title: "Las prioridades cambian con cada sprint",
    text: "Sin un ancla estratégica de largo plazo, el roadmap se convierte en una lista de urgencias. Cada semana se reinicia la conversación sobre qué importa más.",
  },
];

const EOS_FOR_TECH = [
  {
    icon: Layers,
    num: "01",
    title: "Agile + EOS = Ejecución con dirección",
    text: "EOS no reemplaza tu metodología ágil — la complementa. EOS da la estructura estratégica trimestral y anual; Agile maneja la ejecución operativa del equipo técnico.",
  },
  {
    icon: Target,
    num: "02",
    title: "La estrategia se traduce al roadmap",
    text: "Los Rocks Trimestrales de EOS conectan los objetivos del negocio con las prioridades del producto. El equipo técnico deja de trabajar en el vacío — sabe por qué importa lo que construye.",
  },
  {
    icon: TrendingUp,
    num: "03",
    title: "Liderazgo que escala con la empresa",
    text: "A medida que creces, el liderazgo debe evolucionar. EOS estructura el equipo directivo para que pueda operar con autonomía, foco y accountability — sin depender del fundador en cada decisión.",
  },
];

export default function EOSEmpresasTecnologia() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "EOS para Empresas de Tecnología | Carlos Garcín Implementador EOS Chile",
      description: "EOS ayuda a empresas de tecnología a escalar con estructura de liderazgo sin perder agilidad. Carlos Garcín, Implementador EOS oficial en Chile.",
      path: "/eos-empresas-tecnologia",
      keywords: "EOS empresas tecnología, EOS startups, EOS y agile, escalar empresa tech Chile",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="EOS para Empresas de Tecnología"
          title="Tus ingenieros construyen bien. ¿Tu empresa también?"
          subtitle="Las empresas tech dominan la ejecución técnica. EOS les da la estructura de negocio que falta para escalar con foco estratégico, sin perder la agilidad que las hace fuertes."
          withCta
        />

        {/* Desafíos del crecimiento tech */}
        <section data-testid="tech-challenges" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">El problema recurrente</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Los desafíos que comparten{" "}
                <span className="text-[#F26522]">casi todas las empresas tech</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                La agilidad técnica es una ventaja competitiva enorme. Pero sin estructura de negocio, se convierte en velocidad sin dirección.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {CHALLENGES.map((c, idx) => (
                <article
                  key={idx}
                  data-testid={`tech-challenge-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-slate-200 to-slate-100" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                      <c.icon className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                      {c.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{c.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{c.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* EOS para tech */}
        <section data-testid="eos-for-tech" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">EOS en empresas tech</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Estructura de negocio.{" "}
                <span className="text-[#F26522]">Sin perder agilidad.</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                EOS se integra naturalmente a la cultura tech. No peleamos con Scrum ni con OKRs — los potenciamos dándoles el contexto estratégico que les falta.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {EOS_FOR_TECH.map((e, idx) => (
                <article
                  key={idx}
                  data-testid={`eos-tech-card-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <e.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {e.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{e.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{e.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BeforeAfter />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
